import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useScannerContext } from '@/contexts/ScannerContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Scissors,
  Loader2,
  CheckCircle,
  Building2,
  Package,
  Layers,
  RefreshCw,
  AlertTriangle,
  Camera,
  Minus,
  Plus,
  Search,
  Timer,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  Archive,
} from 'lucide-react';

// ============ Tipos ============

interface Shelf {
  id: string;
  code: string;
  name: string;
  min_skill_level: 'A' | 'B' | 'C';
  shelf_type: 'standard' | 'paint' | 'composite';
  sort_order: number;
}

interface QueueItem {
  processRecordId: string;
  processStatus: string;
  workOrderId: string;
  orderNumber: string;
  productName: string;
  customer: string | null;
  quantity: number;
  productModelId: string | null;
  shelfId: string | null;
  cuttingLevel: 'A' | 'B' | 'C' | null;
  maxMinutes: number | null;
  finishType: string | null;
  complexity: string | null;
  requiresPaint: boolean;
  completedCuts: number;
  inProgressCuts: number;
}

interface ActiveCut {
  id: string;
  batch_id: string;
  work_order_id: string;
  started_at: string;
  product_model_id: string | null;
}

interface ActiveBatch {
  batchId: string;
  cutIds: string[];
  workOrderId: string;
  orderNumber: string;
  productName: string;
  count: number;
  startedAt: string;
  maxMinutes: number | null;
}

const LEVEL_RANK: Record<string, number> = { A: 3, B: 2, C: 1 };
const SHELF_TYPE_LABEL: Record<string, string> = {
  standard: 'Estándar',
  paint: 'Pintura',
  composite: 'Compuestas',
};

// Sube una foto al endpoint /upload del servidor (mismo patrón que PnPhotoUpload).
// IMPORTANTE: 'folder' debe ir ANTES de 'file' para que multer lo reciba.
async function uploadCutPhoto(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('folder', 'workshop_cuts');
  fd.append('file', file);
  const res = await fetch('/upload', { method: 'POST', body: fd });
  if (!res.ok) throw new Error('Error al subir la foto al servidor');
  const j = await res.json();
  return j.url as string;
}

export default function WorkshopCutting() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { nfcBridge } = useScannerContext();

  const [loading, setLoading] = useState(true);
  const [skillLevel, setSkillLevel] = useState<'A' | 'B' | 'C' | null>(null);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);
  const [selection, setSelection] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const overtimeNotified = useRef<Set<string>>(new Set());

  // Diálogo de finalización de lote
  const [finishBatch, setFinishBatch] = useState<ActiveBatch | null>(null);
  const [finishResult, setFinishResult] = useState<'ok' | 'defect'>('ok');
  const [finishNotes, setFinishNotes] = useState('');
  const [finishPhotos, setFinishPhotos] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [savingFinish, setSavingFinish] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Reloj para cronómetros
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // ============ Carga de datos ============

  const loadAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Nivel del operario (los perfiles sin nivel -supervisores/admin- ven todo)
      const { data: profileData } = await api
        .from('profiles')
        .select('skill_level')
        .eq('id', user.id)
        .single();
      setSkillLevel(profileData?.skill_level || null);

      // Estanterías activas
      const { data: shelvesData } = await api
        .from('workshop_shelves')
        .select('id,code,name,min_skill_level,shelf_type,sort_order')
        .eq('is_active', true)
        .order('sort_order');
      setShelves(shelvesData || []);

      // Proceso de corte taller
      const { data: processData } = await api
        .from('manufacturing_processes')
        .select('id')
        .eq('code', 'cutting_workshop')
        .single();
      if (!processData) {
        setQueue([]);
        setActiveBatches([]);
        return;
      }

      // Registros de proceso de corte pendientes/en curso
      const { data: records } = await api
        .from('process_records')
        .select('id,work_order_id,status')
        .eq('process_id', processData.id);
      const pendingRecords = (records || []).filter(
        (r: any) => r.status === 'pending' || r.status === 'in_progress'
      );

      const woIds = [...new Set(pendingRecords.map((r: any) => r.work_order_id).filter(Boolean))];

      // OTs (consulta directa + mapa por id; sin embeds anidados)
      let workOrders: any[] = [];
      if (woIds.length > 0) {
        const { data: woData } = await api
          .from('work_orders')
          .select('id,order_number,product_name,customer,quantity,product_model_id,status,is_active')
          .in('id', woIds);
        workOrders = (woData || []).filter(
          (wo: any) => wo.is_active !== false && wo.status !== 'cancelled'
        );
      }
      const woMap = new Map(workOrders.map((wo: any) => [wo.id, wo]));

      // Modelos de producto de esas OTs
      const pmIds = [...new Set(workOrders.map((wo: any) => wo.product_model_id).filter(Boolean))];
      let models: any[] = [];
      if (pmIds.length > 0) {
        const { data: pmData } = await api
          .from('product_models')
          .select('id,cutting_shelf_id,cutting_skill_level,cutting_max_minutes,finish_type,piece_complexity,requires_paint')
          .in('id', pmIds);
        models = pmData || [];
      }
      const pmMap = new Map(models.map((m: any) => [m.id, m]));

      // Cortes ya registrados de esas OTs
      let cuts: any[] = [];
      if (woIds.length > 0) {
        const { data: cutsData } = await api
          .from('workshop_cuts')
          .select('id,batch_id,work_order_id,operator_id,status,started_at,product_model_id')
          .in('work_order_id', woIds);
        cuts = cutsData || [];
      }

      // Cola
      const items: QueueItem[] = pendingRecords
        .filter((r: any) => woMap.has(r.work_order_id))
        .map((r: any) => {
          const wo = woMap.get(r.work_order_id);
          const pm = wo.product_model_id ? pmMap.get(wo.product_model_id) : null;
          const woCuts = cuts.filter((c: any) => c.work_order_id === wo.id);
          return {
            processRecordId: r.id,
            processStatus: r.status,
            workOrderId: wo.id,
            orderNumber: wo.order_number,
            productName: wo.product_name,
            customer: wo.customer || null,
            quantity: wo.quantity || 1,
            productModelId: wo.product_model_id,
            shelfId: pm?.cutting_shelf_id || null,
            cuttingLevel: pm?.cutting_skill_level || null,
            maxMinutes: pm?.cutting_max_minutes || null,
            finishType: pm?.finish_type || null,
            complexity: pm?.piece_complexity || null,
            requiresPaint: !!pm?.requires_paint,
            completedCuts: woCuts.filter((c: any) => c.status === 'completed').length,
            inProgressCuts: woCuts.filter((c: any) => c.status === 'in_progress').length,
          };
        });
      setQueue(items);

      // Lotes en curso del operario actual
      const myCuts: ActiveCut[] = cuts.filter(
        (c: any) => c.status === 'in_progress' && c.operator_id === user.id
      );
      const byBatch = new Map<string, ActiveCut[]>();
      myCuts.forEach((c) => {
        const key = c.batch_id || c.id;
        if (!byBatch.has(key)) byBatch.set(key, []);
        byBatch.get(key)!.push(c);
      });
      const batches: ActiveBatch[] = [...byBatch.entries()].map(([batchId, bc]) => {
        const wo = woMap.get(bc[0].work_order_id);
        const pm = bc[0].product_model_id ? pmMap.get(bc[0].product_model_id) : null;
        return {
          batchId,
          cutIds: bc.map((c) => c.id),
          workOrderId: bc[0].work_order_id,
          orderNumber: wo?.order_number || '—',
          productName: wo?.product_name || '—',
          count: bc.length,
          startedAt: bc[0].started_at,
          maxMinutes: pm?.cutting_max_minutes || null,
        };
      });
      setActiveBatches(batches);
    } catch (error: any) {
      console.error('Error cargando cola de corte:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la cola de corte',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Lector NFC del PC: al leer un tag de OT, la buscamos en la cola
  useEffect(() => {
    const handleBridgeScan = async () => {
      if (!nfcBridge.lastRead?.uid) return;
      try {
        const { data: tagData } = await api
          .from('nfc_tags')
          .select('work_order_id,notes')
          .eq('tag_uid', nfcBridge.lastRead.uid)
          .single();
        let woId = tagData?.work_order_id;
        if (!woId && tagData?.notes) {
          try {
            const payload = JSON.parse(tagData.notes);
            woId = payload.work_order_id || payload.workOrderId || payload.work_order?.id;
          } catch { /* notes sin JSON */ }
        }
        if (woId) {
          const item = queue.find((q) => q.workOrderId === woId);
          if (item) {
            setSearch(item.orderNumber);
            toast({ title: 'Pieza localizada', description: `OT ${item.orderNumber} filtrada en la cola` });
          } else {
            toast({
              title: 'OT no está en la cola de corte',
              description: 'La orden escaneada no tiene corte de taller pendiente',
              variant: 'destructive',
            });
          }
        }
      } catch (error) {
        console.error('Bridge scan error:', error);
      }
    };
    handleBridgeScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfcBridge.lastRead]);

  // ============ Agrupación por estantería + filtro por nivel ============

  const visibleShelfGroups = useMemo(() => {
    const myRank = skillLevel ? LEVEL_RANK[skillLevel] : 99; // sin nivel => ve todo
    const q = search.trim().toLowerCase();

    const matchesSearch = (item: QueueItem) =>
      !q ||
      item.orderNumber.toLowerCase().includes(q) ||
      (item.productName || '').toLowerCase().includes(q) ||
      (item.customer || '').toLowerCase().includes(q);

    const pendingOf = (item: QueueItem) =>
      Math.max(0, item.quantity - item.completedCuts - item.inProgressCuts);

    const groups: { shelf: Shelf | null; items: QueueItem[] }[] = [];

    const orderedShelves = [...shelves].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    for (const shelf of orderedShelves) {
      if (LEVEL_RANK[shelf.min_skill_level] > myRank) continue; // nivel insuficiente
      const items = queue.filter(
        (i) => i.shelfId === shelf.id && pendingOf(i) > 0 && matchesSearch(i)
      );
      if (items.length > 0) groups.push({ shelf, items });
    }

    // Piezas sin estantería asignada: visibles para todos (el supervisor debe asignarlas)
    const unassigned = queue.filter(
      (i) => (!i.shelfId || !shelves.some((s) => s.id === i.shelfId)) && pendingOf(i) > 0 && matchesSearch(i)
    );
    if (unassigned.length > 0) groups.push({ shelf: null, items: unassigned });

    return groups;
  }, [queue, shelves, skillLevel, search]);

  // ============ Acciones ============

  const changeSelection = (item: QueueItem, delta: number) => {
    const pending = Math.max(0, item.quantity - item.completedCuts - item.inProgressCuts);
    setSelection((prev) => {
      const current = prev[item.workOrderId] ?? 1;
      const next = Math.min(Math.max(1, current + delta), Math.max(1, pending));
      return { ...prev, [item.workOrderId]: next };
    });
  };

  const handleStartCut = async (item: QueueItem) => {
    if (!user) return;
    const count = Math.min(
      selection[item.workOrderId] ?? 1,
      Math.max(1, item.quantity - item.completedCuts - item.inProgressCuts)
    );
    setStarting(item.workOrderId);
    try {
      const batchId = crypto.randomUUID();
      const startedAt = new Date().toISOString();
      const rows = Array.from({ length: count }, () => ({
        work_order_id: item.workOrderId,
        process_record_id: item.processRecordId,
        product_model_id: item.productModelId,
        operator_id: user.id,
        shelf_id: item.shelfId,
        batch_id: batchId,
        started_at: startedAt,
        status: 'in_progress',
      }));
      const { error } = await api.from('workshop_cuts').insert(rows);
      if (error) throw error;

      // El proceso de la OT pasa a en curso si estaba pendiente
      if (item.processStatus === 'pending') {
        await api
          .from('process_records')
          .update({ status: 'in_progress', started_at: startedAt })
          .eq('id', item.processRecordId);
      }

      toast({
        title: 'Corte iniciado',
        description: `${count} pieza(s) de ${item.productName} — cronómetro en marcha`,
      });
      setSelection((prev) => ({ ...prev, [item.workOrderId]: 1 }));
      await loadAll();
    } catch (error: any) {
      toast({
        title: 'Error al iniciar el corte',
        description: error.message || 'No se pudo registrar el inicio',
        variant: 'destructive',
      });
    } finally {
      setStarting(null);
    }
  };

  const openFinishDialog = (batch: ActiveBatch) => {
    setFinishBatch(batch);
    setFinishResult('ok');
    setFinishNotes('');
    setFinishPhotos([]);
  };

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadCutPhoto(file);
      setFinishPhotos((prev) => [...prev, url]);
    } catch (error: any) {
      toast({ title: 'Error al subir la foto', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleFinishBatch = async () => {
    if (!finishBatch || !user) return;
    setSavingFinish(true);
    try {
      const finishedAt = new Date();
      const durationSeconds = Math.round(
        (finishedAt.getTime() - new Date(finishBatch.startedAt).getTime()) / 1000
      );
      const overtime =
        !!finishBatch.maxMinutes && durationSeconds > finishBatch.maxMinutes * 60;

      for (const cutId of finishBatch.cutIds) {
        const { error } = await api
          .from('workshop_cuts')
          .update({
            status: 'completed',
            result: finishResult,
            finished_at: finishedAt.toISOString(),
            duration_seconds: durationSeconds,
            overtime,
            notes: finishNotes.trim() || null,
            updated_at: finishedAt.toISOString(),
          })
          .eq('id', cutId);
        if (error) throw error;
      }

      // Fotos del lote (asociadas al primer corte del lote)
      if (finishPhotos.length > 0) {
        await api.from('workshop_cut_photos').insert(
          finishPhotos.map((url) => ({ cut_id: finishBatch.cutIds[0], photo_url: url }))
        );
      }

      // Si ya no quedan piezas pendientes, completar el proceso de la OT
      const item = queue.find((q) => q.workOrderId === finishBatch.workOrderId);
      if (item) {
        const totalCompleted = item.completedCuts + finishBatch.count;
        if (totalCompleted >= item.quantity) {
          await api
            .from('process_records')
            .update({
              status: 'completed',
              completed_at: finishedAt.toISOString(),
              completed_by: user.id,
            })
            .eq('id', item.processRecordId);
        }
      }

      toast({
        title: 'Corte completado',
        description: `${finishBatch.count} pieza(s) registradas${overtime ? ' (con exceso de tiempo)' : ''}`,
      });
      setFinishBatch(null);
      await loadAll();
    } catch (error: any) {
      toast({
        title: 'Error al finalizar',
        description: error.message || 'No se pudo registrar el corte',
        variant: 'destructive',
      });
    } finally {
      setSavingFinish(false);
    }
  };

  const handleCancelBatch = async (batch: ActiveBatch) => {
    try {
      for (const cutId of batch.cutIds) {
        await api
          .from('workshop_cuts')
          .update({ status: 'cancelled', updated_at: new Date().toISOString() })
          .eq('id', cutId);
      }
      toast({ title: 'Corte cancelado', description: 'Las piezas vuelven a la cola' });
      await loadAll();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Alerta de exceso de tiempo (una vez por lote)
  useEffect(() => {
    for (const b of activeBatches) {
      if (!b.maxMinutes) continue;
      const elapsedMin = (now - new Date(b.startedAt).getTime()) / 60000;
      if (elapsedMin > b.maxMinutes && !overtimeNotified.current.has(b.batchId)) {
        overtimeNotified.current.add(b.batchId);
        toast({
          title: '⚠️ Exceso de tiempo en corte',
          description: `${b.productName} lleva ${Math.round(elapsedMin)} min (máx. ${b.maxMinutes} min). Avisa al supervisor si hay algún problema.`,
          variant: 'destructive',
        });
      }
    }
  }, [now, activeBatches, toast]);

  const formatElapsed = (startedAt: string) => {
    const s = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };

  // ============ Render ============

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Cabecera */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent flex items-center gap-3">
              <Scissors className="w-8 h-8 text-primary" />
              Corte Taller
            </h1>
            <p className="text-muted-foreground mt-2">
              Piezas pendientes de corte organizadas por estantería
            </p>
          </div>
          <div className="flex items-center gap-2">
            {skillLevel && (
              <Badge variant="secondary" className="text-sm">
                Tu nivel: {skillLevel}
              </Badge>
            )}
            {nfcBridge.isReaderReady && (
              <Badge variant="outline" className="gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Lector NFC conectado
              </Badge>
            )}
            <Button variant="outline" size="icon" onClick={loadAll} title="Recargar">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Cortes en curso del operario */}
        {activeBatches.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Timer className="w-5 h-5 text-primary" />
              Tus cortes en curso
            </h2>
            {activeBatches.map((b) => {
              const elapsedMin = (now - new Date(b.startedAt).getTime()) / 60000;
              const isOvertime = !!b.maxMinutes && elapsedMin > b.maxMinutes;
              const pct = b.maxMinutes ? Math.min(100, (elapsedMin / b.maxMinutes) * 100) : null;
              return (
                <Card
                  key={b.batchId}
                  className={isOvertime ? 'border-red-500 bg-red-500/5' : 'border-primary/40'}
                >
                  <CardContent className="py-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{b.orderNumber}</Badge>
                        <span className="font-medium">{b.productName}</span>
                        <Badge variant="secondary">{b.count} pieza(s)</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`font-mono text-xl font-semibold ${
                            isOvertime ? 'text-red-500' : 'text-primary'
                          }`}
                        >
                          {formatElapsed(b.startedAt)}
                        </span>
                        {b.maxMinutes && (
                          <span className="text-sm text-muted-foreground">
                            / {b.maxMinutes} min
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelBatch(b)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openFinishDialog(b)}
                          className="bg-gradient-magma hover:shadow-magma-hover"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Finalizar
                        </Button>
                      </div>
                    </div>
                    {pct !== null && (
                      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            isOvertime ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-primary'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                    {isOvertime && (
                      <p className="text-sm text-red-500 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Tiempo máximo superado — no dejes la pieza estancada, avisa al supervisor si hay problemas
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Buscador */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por OT, producto o cliente... (o escanea NFC)"
            className="pl-9"
          />
        </div>

        {/* Cola por estanterías */}
        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="ml-3 text-lg">Cargando cola de corte...</span>
            </CardContent>
          </Card>
        ) : visibleShelfGroups.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 space-y-3">
              <CheckCircle className="w-12 h-12 text-green-500" />
              <p className="text-lg font-medium">No hay piezas pendientes de corte</p>
              <p className="text-sm text-muted-foreground">
                {skillLevel
                  ? `Se muestran solo las estanterías de tu nivel (${skillLevel}) e inferiores`
                  : 'La cola está vacía'}
              </p>
            </CardContent>
          </Card>
        ) : (
          visibleShelfGroups.map(({ shelf, items }) => (
            <Card key={shelf?.id || 'none'}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Archive className="w-5 h-5 text-primary" />
                  {shelf ? (
                    <>
                      {shelf.code} — {shelf.name}
                      <Badge variant="secondary">Nivel {shelf.min_skill_level}</Badge>
                      {shelf.shelf_type !== 'standard' && (
                        <Badge
                          variant="outline"
                          className={shelf.shelf_type === 'paint' ? 'border-purple-500 text-purple-500' : ''}
                        >
                          {SHELF_TYPE_LABEL[shelf.shelf_type]}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <>
                      Sin estantería asignada
                      <Badge variant="outline" className="border-amber-500 text-amber-500">
                        Asignar en el modelo de producto
                      </Badge>
                    </>
                  )}
                  <Badge variant="outline" className="ml-auto">
                    {items.length} referencia(s)
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {items.map((item) => {
                  const pending = Math.max(
                    0,
                    item.quantity - item.completedCuts - item.inProgressCuts
                  );
                  const sel = Math.min(selection[item.workOrderId] ?? 1, pending);
                  return (
                    <div
                      key={item.processRecordId}
                      className="flex items-center justify-between flex-wrap gap-3 p-3 rounded-lg border bg-muted/20"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline">{item.orderNumber}</Badge>
                          <span className="font-medium truncate">{item.productName}</span>
                          {item.cuttingLevel && (
                            <Badge variant="secondary">Nivel {item.cuttingLevel}</Badge>
                          )}
                          {item.requiresPaint && (
                            <Badge variant="outline" className="border-purple-500 text-purple-500">
                              Pintura
                            </Badge>
                          )}
                          {item.complexity === 'composite' && (
                            <Badge variant="outline">Compuesta</Badge>
                          )}
                          {item.finishType === 'visual' && (
                            <Badge variant="outline" className="border-blue-500 text-blue-500">
                              Visual
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {item.customer && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {item.customer}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {pending} de {item.quantity} pendiente(s)
                          </span>
                          {item.completedCuts > 0 && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              {item.completedCuts} cortada(s)
                            </span>
                          )}
                          {item.inProgressCuts > 0 && (
                            <span className="flex items-center gap-1 text-blue-500">
                              <Layers className="w-3 h-3" />
                              {item.inProgressCuts} en curso
                            </span>
                          )}
                          {item.maxMinutes && (
                            <span className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              máx. {item.maxMinutes} min
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Selección de varias piezas iguales */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center border rounded-lg">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            disabled={sel <= 1}
                            onClick={() => changeSelection(item, -1)}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-10 text-center font-semibold">{sel}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            disabled={sel >= pending}
                            onClick={() => changeSelection(item, 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button
                          onClick={() => handleStartCut(item)}
                          disabled={starting === item.workOrderId || pending === 0}
                          className="bg-gradient-magma hover:shadow-magma-hover"
                        >
                          {starting === item.workOrderId ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Scissors className="w-4 h-4 mr-2" />
                          )}
                          Cortar {sel > 1 ? `${sel} piezas` : 'pieza'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Diálogo de finalización */}
      <Dialog open={!!finishBatch} onOpenChange={(o) => !o && !savingFinish && setFinishBatch(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Finalizar corte — {finishBatch?.productName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{finishBatch?.orderNumber}</Badge>
              <span>{finishBatch?.count} pieza(s)</span>
              {finishBatch && (
                <span className="font-mono">
                  ⏱ {formatElapsed(finishBatch.startedAt)}
                </span>
              )}
            </div>

            {/* Resultado */}
            <div className="space-y-2">
              <Label>Resultado del corte</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={finishResult === 'ok' ? 'default' : 'outline'}
                  className={finishResult === 'ok' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setFinishResult('ok')}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Bien cortada
                </Button>
                <Button
                  type="button"
                  variant={finishResult === 'defect' ? 'default' : 'outline'}
                  className={finishResult === 'defect' ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setFinishResult('defect')}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  Con defecto
                </Button>
              </div>
            </div>

            {/* Observaciones */}
            <div className="space-y-2">
              <Label>Observaciones (problemas, recomendaciones)</Label>
              <Textarea
                value={finishNotes}
                onChange={(e) => setFinishNotes(e.target.value)}
                placeholder="Ej: la fibra se astilla en la esquina superior, recomendable cuchilla nueva..."
                rows={3}
              />
            </div>

            {/* Fotos */}
            <div className="space-y-2">
              <Label>Fotos del proceso</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {finishPhotos.map((url, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={url}
                      alt={`Foto ${idx + 1}`}
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => setFinishPhotos((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleAddPhoto}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploadingPhoto}
                  onClick={() => photoInputRef.current?.click()}
                  className="w-16 h-16 flex flex-col items-center justify-center gap-1"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Camera className="w-5 h-5" />
                      <span className="text-[10px]">Foto</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFinishBatch(null)}
              disabled={savingFinish}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleFinishBatch}
              disabled={savingFinish}
              className="bg-gradient-magma hover:shadow-magma-hover"
            >
              {savingFinish ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirmar corte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
